import React, { useEffect, useState } from 'react';
import { walletServices } from '../services/WalletServices';
import { planServices } from '../services/PlanServices';
import { Link } from 'react-router-dom';

const Wallet = () => {
    const [walletData, setWalletData] = useState({});
    const [walletTransactions, setWalletTransactions] = useState([]);
    const id = localStorage.getItem('id');
    const [purchasedPlans, setPurchasedPlans] = useState([]);
    
    useEffect(() => {
        getWalletDetails();
        fetchWalletTransactions();
    },[]);

    useEffect(() => {
        const fetchPlansByUser = async (id) => {
            try {
                const response = await planServices.getPlansByUser(id);
                if (response.status === 200) {
                    const purchasedPlans = response.data.data;
                    setPurchasedPlans(purchasedPlans);
                } else {
                    console.error('Error fetching plans:', response.data?.message || 'Unknown error');
                }
            } catch (error) {
                console.error('Error fetching the plans:', error);
            }
        };

        if (id) { // Add check to ensure id exists
            fetchPlansByUser(id);
        }
    }, [id]);

    const getWalletDetails = async () => {
        try {
            const response = await walletServices.getOrCreateWallet(id);
            if (response.status === 200 || response.status === 201) {
                setWalletData(response.data.wallet); // assuming backend responds with `wallet`
            } else {
                console.error("Unexpected response while getting/creating wallet:", response);
            }
        } catch (error) {
            console.error('Error fetching or creating wallet:', error);
        }
    };

    const fetchWalletTransactions = async () => {
        try {
            const response = await walletServices.getWalletTransactions(id);
            if (response.status === 200) {
                setWalletTransactions(response.data);
            }
        } catch (error) {
            console.error('Error fetching wallet transactions:', error);
        }
    };

    return (
        <div id="cryptoon-layout" className="theme-orange">
            <div className="main px-lg-4 px-md-4">
                <div className="body-header border-bottom d-flex py-3">
                    <div className="container-xxl">
                        <div className="row align-items-center">
                            <div className="col">
                                <h1 className="h4 mt-1">Wallet Overview</h1>
                            </div>
                            <div className="col-auto">
                                <Link
                                    className="btn btn-dark lift"
                                    to="/user/wallet/topup"
                                >Topup Wallet</Link>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="body d-flex py-3">
                    <div className="container-xxl">
                        <div className="row g-3 mb-3 row-deck">
                            <div className="col-xl-7">
                                <div className="card">
                                    <div className="card-header py-3 bg-transparent border-bottom">
                                        <h6 className="mb-0 fw-bold">Balance Details</h6>
                                    </div>
                                    <div className="card-body">
                                        <h3>{walletData.balance ?? 0}</h3>
                                        <div className="mt-3 text-muted small">Total Earned Amount</div>
                                        <h5>{walletData.total_earned ?? 0}</h5>
                                        <div className="mt-3 text-muted small">Total Spent Amount</div>
                                        <h5>{walletData.total_spent ?? 0}</h5>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-5">
                                <div className="card">
                                    <div className="card-header py-3 bg-transparent border-bottom">
                                        <h6 className="mb-0 fw-bold">Current Plans</h6>
                                    </div>
                                    <div className="card-body">
                                        {purchasedPlans.length > 0 ? (
                                            purchasedPlans.map((plan, idx) => (
                                                <div key={plan._id || idx} className="d-flex justify-content-between flex-wrap mb-2">
                                                    <div>
                                                        {/* Use planDetails if available, otherwise fallback to existing data */}
                                                        <div>
                                                            {plan.planDetails?.plan_type_id?.plan_title || 
                                                            plan.planDetails?.planCategory || 
                                                            plan.planCategory || 
                                                            'N/A'}
                                                        </div>
                                                        <small className="text-muted">
                                                            Invested: ${plan.investmentAmount || 'N/A'} | 
                                                            Expected: ${plan.expectedReturn || 'N/A'}
                                                            {plan.planDetails?.start_price && plan.planDetails?.end_price && 
                                                            ` | Range: ${plan.planDetails.start_price} ~ ${plan.planDetails.end_price}`
                                                            }
                                                        </small>
                                                        <small className="text-muted d-block">
                                                            Status: {plan.status || 'N/A'} | 
                                                            Type: {plan.planType || 'N/A'} | 
                                                            Lock-in: {plan.lockInPeriod} {plan.lockInPeriodUnit}
                                                        </small>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div>No Plans Found</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-xl-12">
                                <div className="card no-bg">
                                    <div className="card-header py-3">
                                        <h6 className="mb-0 fw-bold">Transaction History</h6>
                                    </div>
                                    <div className="card-body">
                                        <table className="priceTable table table-hover custom-table table-bordered align-middle mb-0" style={{ width: "100%" }}>
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Type</th>
                                                    <th>Asset</th>
                                                    <th>Amount</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {walletTransactions.length > 0 ? (
                                                    walletTransactions.map((txn) => (
                                                        <tr key={txn._id}>
                                                            <td>{new Date(txn.created_at).toLocaleString()}</td>
                                                            <td>{txn.transaction_type === 'credit' ? 'Credit' : 'Debit'}</td>
                                                            <td>{txn.description || 'N/A'}</td>
                                                            <td>{txn.amount.toFixed(2)}</td>
                                                            <td><span className="color-price-up">Completed</span></td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="text-center">No transactions found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Wallet;